import GridTable from '../../table/GridTable';
import {
  ColDef,
  ICellRenderer,
  ICellRendererParams,
  RowClassParams,
} from 'ag-grid-community';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import { AgGridReact } from 'ag-grid-react';
import queryString from 'query-string';
import {
  IBokbiHistory,
  useBokbiHistoryStore,
} from '../../../store/useBokbiHistory';
import BokbiHistoryFilter from './filter/BokbiHistoryFilter';
import ChargeStatus from '../../table/ChargeStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import BokbiPurchaseOrderModal from './modal/BokbiPurchaseOrderModal';
import { useFranchiseStore } from '../../../store/useFranchise';

/**
 * @author taulmax
 * @date 2025.02.04
 * @description 복비 내역 목록 페이지
 */
const BokbiHistoryList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedPurchaseOrderId, setSelectedPurchaseOrderId] = useState<string>('');
  const [isPurchaseOrderModalOpen, setIsPurchaseOrderModalOpen] = useState(false);

  const {
    bokbiHistory,
    bokbiHistoryPage,
    getBokbiHistoryLoading,
    getBokbiHistory,
  } = useBokbiHistoryStore();
  
  const { getMyFranchise } = useFranchiseStore();

  const gridRef = useRef<AgGridReact<IBokbiHistory>>(null);

  const colDefs: ColDef<IBokbiHistory>[] = [
    {
      headerName: '유형',
      field: 'chargeType',
      cellRenderer: (props: ICellRendererParams) => {
        if (props.value) {
          return <ChargeStatus status={props.value} code={props.value} />;
        }
      },
      flex: 1,
    },
    {
      headerName: '가맹점',
      field: 'companyName',
      flex: 2,
    },
    {
      headerName: '금액',
      field: 'amount',
      flex: 2,
      valueFormatter: params => {
        const value = Number(params.value);
        return isNaN(value) ? params.value : value.toLocaleString();
      },
    },
    {
      headerName: '날짜',
      field: 'createAt',
      valueFormatter: param => formatDate(param.value).timestamp,
      flex: 3,
    },
    {
      headerName: '잔액',
      field: 'balance',
      flex: 2,
      valueFormatter: params => {
        const value = Number(params.value);
        return isNaN(value) ? params.value : value.toLocaleString();
      },
    },
    {
      headerName: '메모',
      field: 'memo',
      flex: 4,
    },
    {
      headerName: '주문서',
      field: 'franchiseChargeHstId',
      flex: 1,
      cellRenderer: (params: ICellRendererParams) => {
        if (params.data?.purchaseOrderId && params.data?.purchaseOrderId) {
          return (
            <button
              onClick={() => {
                setSelectedPurchaseOrderId(params.data.purchaseOrderId);
                setIsPurchaseOrderModalOpen(true);
              }}
              className="saju_table_button px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors text-sm font-medium"
            >
              주문서
            </button>
          );
        }
        return null;
      },
    },
  ];

  const getFilteredData = () => {
    const parsedQuery = queryString.parse(location.search);
    const chargeType = (parsedQuery.chargeType as 'CHARGE' | 'ORDER_PAY' | 'CANCEL') || '';
    const franchiseId = (parsedQuery.franchiseId as string) || '';
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;
    const StartDt = parsedQuery.StartDt as string;
    const EndDt = parsedQuery.EndDt as string;

    const queryParams = {
      chargeType,
      franchiseId,
      size,
      StartDt,
      EndDt,
    };

    updateUrlWithParams(queryParams, navigate);
    getBokbiHistory({
      chargeType,
      franchiseId,
      StartDt,
      EndDt,
      size,
    });
  };

  useEffect(() => {
    const parsedQuery = queryString.parse(location.search);
    const chargeType = (parsedQuery.chargeType as 'CHARGE' | 'ORDER_PAY' | 'CANCEL') || '';
    const franchiseId = (parsedQuery.franchiseId as string) || '';
    const StartDt = (parsedQuery.StartDt as string) || '';
    const EndDt = (parsedQuery.EndDt as string) || '';
    const page = parsedQuery.page ? Number(parsedQuery.page) : 0;
    const size = parsedQuery.size ? Number(parsedQuery.size) : 20;

    getBokbiHistory({
      chargeType,
      franchiseId,
      StartDt,
      EndDt,
      page,
      size,
    });
  }, [getBokbiHistory, location.search]);

  const onPageChange = useCallback(
    (page: number) => {
      const parsedQuery = queryString.parse(location.search);
      const updatedQuery = { ...parsedQuery, page: page.toString() };

      updateUrlWithParams(updatedQuery, navigate);
      getBokbiHistory({
        page,
        chargeType: (parsedQuery.chargeType as 'CHARGE' | 'ORDER_PAY' | 'CANCEL') || '',
        size: parseInt(parsedQuery.size as string) || 20,
        franchiseId: (parsedQuery.franchiseId as string) || '',
        StartDt: (parsedQuery.StartDt as string) || '',
        EndDt: (parsedQuery.EndDt as string) || '',
      });
    },
    [getBokbiHistory, location.search, navigate]
  );

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !items-center !justify-between !py-4">
        <BokbiHistoryFilter getFilteredData={getFilteredData} />
      </div>

      <div className="!flex-1 !min-h-0">
        <GridTable<IBokbiHistory>
          gridRef={gridRef}
          height="100%"
          isLoading={getBokbiHistoryLoading}
          rowData={bokbiHistory}
          columnDefs={colDefs}
          rowClassRules={{
            order_pay_row: (params: RowClassParams) =>
              params.data.chargeType === 'ORDER_PAY',
            charge_row: (params: RowClassParams) =>
              params.data.chargeType === 'CHARGE',
            cancel_row: (params: RowClassParams) =>
                params.data.chargeType === 'CANCEL',
          }}
        />
      </div>

      {/* 페이지네이션 */}
      {bokbiHistoryPage && bokbiHistoryPage.totalPages > 1 && (
        <div className="!w-full !mt-6">
          <div className="!flex !justify-center !items-center !space-x-1">
            {/* 맨 처음으로 */}
            <button
              onClick={() => onPageChange(0)}
              disabled={bokbiHistoryPage.pageable.pageNumber === 0}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3 !-ml-1" />
            </button>
            
            {/* 이전 페이지 */}
            <button
              onClick={() => onPageChange(Math.max(0, bokbiHistoryPage.pageable.pageNumber - 1))}
              disabled={bokbiHistoryPage.pageable.pageNumber === 0}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronLeft} className="!w-3 !h-3" />
            </button>
            
            {/* 페이지 번호들 */}
            <div className="!flex !space-x-0.5">
              {(() => {
                const currentPage = bokbiHistoryPage.pageable.pageNumber;
                const totalPages = bokbiHistoryPage.totalPages;
                let startPage = Math.max(0, currentPage - 4);
                let endPage = Math.min(totalPages - 1, startPage + 9);
                
                if (endPage - startPage < 9) {
                  startPage = Math.max(0, endPage - 9);
                }
                
                return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(pageNum => (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`!px-2.5 !py-1 !text-sm !rounded !transition-colors !duration-200 ${
                      pageNum === currentPage
                        ? '!bg-blue-600 !text-white'
                        : 'hover:!bg-gray-100 !text-gray-700'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                ));
              })()}
            </div>
            
            {/* 다음 페이지 */}
            <button
              onClick={() => onPageChange(Math.min(bokbiHistoryPage.totalPages - 1, bokbiHistoryPage.pageable.pageNumber + 1))}
              disabled={bokbiHistoryPage.pageable.pageNumber === bokbiHistoryPage.totalPages - 1}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
            </button>
            
            {/* 맨 마지막으로 */}
            <button
              onClick={() => onPageChange(bokbiHistoryPage.totalPages - 1)}
              disabled={bokbiHistoryPage.pageable.pageNumber === bokbiHistoryPage.totalPages - 1}
              className="!px-2 !py-1.5 !text-sm !rounded !border !border-gray-300 hover:!bg-gray-50 disabled:!opacity-50 disabled:!cursor-not-allowed !transition-colors !duration-200"
            >
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3" />
              <FontAwesomeIcon icon={faChevronRight} className="!w-3 !h-3 !-ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* 주문서 상세 모달 */}
      <BokbiPurchaseOrderModal
        isOpen={isPurchaseOrderModalOpen}
        onClose={() => {
          setIsPurchaseOrderModalOpen(false);
          setSelectedPurchaseOrderId('');
        }}
        purchaseOrderId={selectedPurchaseOrderId}
        onSuccess={async () => {
          // 현재 페이지의 데이터를 다시 불러오기
          const parsedQuery = queryString.parse(location.search);
          getBokbiHistory({
            page: parseInt(parsedQuery.page as string) || 0,
            chargeType: (parsedQuery.chargeType as 'CHARGE' | 'ORDER_PAY' | 'CANCEL') || '',
            size: parseInt(parsedQuery.size as string) || 20,
            franchiseId: (parsedQuery.franchiseId as string) || '',
            StartDt: (parsedQuery.StartDt as string) || '',
            EndDt: (parsedQuery.EndDt as string) || '',
          });
          
          // 복비 잔액 업데이트를 위해 본인 가맹점 정보 다시 불러오기
          await getMyFranchise();
        }}
      />
    </div>
  );
};

export default BokbiHistoryList;
